/* eslint-disable react/prop-types */
import React from 'react';
import { Tooltip, IconButton } from '@cambridgeassessment/cambridge-ui';
import {
  ConsortiumCardSummaryContainer,
  ConsortiumCardSummary,
  CardActions,
  ConsortiumCentresContainer,
  StyledExpandableCard,
  HeaderContainer,
  StyledConsortiumText,
  StyledConsortiumDescription,
  StyledTitle,
  StyledText,
} from './ConsortiumCard.styles';
import { useHistory } from 'react-router-dom';
import { Consortium, ConsortiumResponse } from '@cambridgeassessment/lib-ee-commons';
import { DeleteOutlineOutlined, EditOutlined, FileCopyOutlined } from '@material-ui/icons';
import { currentTime } from '../../../../util/time';
import moment from 'moment';

interface Props {
  consortium: ConsortiumResponse;
  transitionProps?: unknown;
  isExpanded: boolean;
  index: number;
  onEditConsortium: (id: string) => void;
  onDelete: (id: string, name: string) => void;
}

export const ConsortiumCard: React.FC<Props> = ({
  consortium,
  transitionProps,
  onDelete,
  onEditConsortium,
  isExpanded,
  index,
}) => {
  const history = useHistory();

  const onEditClick = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>): void => {
    e.stopPropagation();
    if (!consortium.samples.length) {
      history.push(`/manageConsortia/editConsortium/${consortium._id}`);
    }
    onEditConsortium(consortium._id);
  };

  const onDuplicateClick = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>): void => {
    e.stopPropagation();
    history.push(`/manageConsortia/createNewConsortium/${consortium._id}`);
  };

  const onDeleteClick = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>): void => {
    e.stopPropagation();
    onDelete(consortium._id, consortium.name);
  };

  const constructComponentsText = (components: Consortium['components']) => {
    let componentsText = '';
    const result = components.reduce(function (r, a) {
      r[a.assessmentCode] = r[a.assessmentCode] || [];
      r[a.assessmentCode].push(a);
      return r;
    }, Object.create(null));
    for (const key of Object.keys(result)) {
      const mergedComponentsText = result[key].reduce(
        (acc: string, curr: { number: string }) => `${acc}${curr.number}, `,
        `${key}/`,
      );
      componentsText = `${componentsText}${mergedComponentsText}`;
    }
    return `${componentsText.slice(0, -2)}. `;
  };

  const summary = (
    <ConsortiumCardSummaryContainer data-qa-id={`consortium-card-${index}`}>
      <HeaderContainer>
        <StyledTitle data-qa-id={`consortium-name-${consortium.name}`}>{consortium.name}</StyledTitle>
        <CardActions>
          {consortium.series.year >= currentTime().year() ? (
            <Tooltip
              arrow={true}
              placement='top-start'
              dataQaId={`edit-consortium-${consortium.name}`}
              title='Edit consortium'
            >
              <span>
                <IconButton color='primary' onClick={onEditClick} qaId={`button-edit-${consortium.name}`}>
                  <EditOutlined />
                </IconButton>
              </span>
            </Tooltip>
          ) : (
            <Tooltip
              arrow={true}
              placement='top-start'
              dataQaId={`duplicate-consortium-${consortium.name}`}
              title='Duplicate a consortium from a past series into a current series'
            >
              <span>
                <IconButton color='primary' onClick={onDuplicateClick} qaId={`button-duplicate-${consortium.name}`}>
                  <FileCopyOutlined />
                </IconButton>
              </span>
            </Tooltip>
          )}

          <Tooltip
            arrow={true}
            placement='top-start'
            dataQaId={`remove-consortium-${consortium.name}`}
            title='Remove consortium'
          >
            <span>
              <IconButton color='primary' onClick={onDeleteClick} qaId={`button-delete-${consortium.name}`}>
                <DeleteOutlineOutlined />
              </IconButton>
            </span>
          </Tooltip>
        </CardActions>
      </HeaderContainer>
      <ConsortiumCardSummary>
        <StyledConsortiumText data-qa-id={`consortium-specifications-${consortium.name}`}>
          {constructComponentsText(consortium.components)}
        </StyledConsortiumText>
        <StyledConsortiumText data-qa-id={`consortium-series-${consortium.name}`}>
          {consortium.series.name}
        </StyledConsortiumText>
        <StyledConsortiumDescription data-qa-id={`consortium-description-${consortium.name}`}>
          {consortium.description}
        </StyledConsortiumDescription>
      </ConsortiumCardSummary>
    </ConsortiumCardSummaryContainer>
  );

  return (
    <StyledExpandableCard summary={summary} TransitionProps={transitionProps} expanded={isExpanded}>
      <StyledText>Last modified: {moment(consortium.lastUpdated).format('H:mm on DD/MM/YYYY')}</StyledText>
      <ConsortiumCentresContainer>
        {consortium.centres.map((centre) => (
          <div key={`${centre.id}-${centre.name}`}>
            {centre.id} - {centre.name}
          </div>
        ))}
      </ConsortiumCentresContainer>
    </StyledExpandableCard>
  );
};

export default ConsortiumCard;
